import type { ReactNode } from 'react';
import { AlertTriangle, CheckCircle2, Loader2 } from 'lucide-react';

interface AlertDialogProps {
  open: boolean;
  title: string;
  description?: ReactNode;
  /** Bullet list of detailed messages (e.g. why records could not be deleted). */
  messages?: ReactNode[];
  tone?: 'danger' | 'success';
  closeLabel?: string;
  /** Optional destructive action rendered next to Close (e.g. "Delete Anyway"). */
  actionLabel?: string;
  onAction?: () => void;
  actionLoading?: boolean;
  onClose: () => void;
}

/**
 * Reusable informational modal for surfacing clear, detailed messages that must
 * not be missed. Used by the Trash pages to explain why a permanent delete was
 * blocked (instead of a transient toast).
 */
export function AlertDialog({
  open,
  title,
  description,
  messages = [],
  tone = 'danger',
  closeLabel = 'Close',
  actionLabel,
  onAction,
  actionLoading = false,
  onClose,
}: AlertDialogProps) {
  if (!open) return null;

  const isDanger = tone === 'danger';
  const iconWrap = isDanger ? 'bg-danger/10 text-danger' : 'bg-success/10 text-success';

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      role="alertdialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className="bg-surface rounded-xl border border-border shadow-xl w-full max-w-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 space-y-3">
          <div className="flex items-start gap-3">
            <div className={`p-2 rounded-lg shrink-0 ${iconWrap}`}>
              {isDanger ? <AlertTriangle size={20} /> : <CheckCircle2 size={20} />}
            </div>
            <div className="space-y-1 min-w-0 flex-1">
              <h3 className="text-lg font-semibold text-text">{title}</h3>
              {description && <div className="text-sm text-text-muted">{description}</div>}
              {messages.length > 0 && (
                <ul className="mt-2 space-y-1.5 text-sm text-text-muted list-disc pl-5 max-h-64 overflow-y-auto">
                  {messages.map((message, index) => (
                    <li key={index}>{message}</li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end px-6 py-4 border-t border-border">
          {onAction && actionLabel && (
            <button
              type="button"
              onClick={onAction}
              disabled={actionLoading}
              className="mr-auto px-4 py-2 bg-danger text-white rounded-lg hover:bg-danger/90 transition-colors text-sm font-medium disabled:opacity-50 flex items-center gap-2"
            >
              {actionLoading && <Loader2 size={16} className="animate-spin" />}
              {actionLabel}
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium"
          >
            {closeLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
